import React, { useState, useEffect } from 'react';
import { formatTimeAgo } from './utils';
import api from './api';

const Thread = ({ thread, boardName, onBack, user, onLoginRequired, onViewProfile }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);

  const isLoggedIn = !!user;

  useEffect(() => {
    fetchComments();
    if (isLoggedIn) {
      api.get(`/threads/${thread.id}/follow`)
        .then(res => setIsFollowing(res.data.following))
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread.id]);

  const fetchComments = async () => {
    try {
      const response = await api.get(`/threads/${thread.id}/comments`);
      setComments(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching comments:', error);
      setLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim() || !user || submitting) return;

    setSubmitting(true);
    try {
      const response = await api.post(`/threads/${thread.id}/comments`, {
        content: newComment.trim(),
      });

      setComments([...comments, response.data]);
      setNewComment('');
    } catch (error) {
      console.error('Error posting comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleFollow = async () => {
    try {
      if (isFollowing) {
        await api.delete(`/threads/${thread.id}/follow`);
        setIsFollowing(false);
      } else {
        await api.post(`/threads/${thread.id}/follow`);
        setIsFollowing(true);
      }
    } catch {
      // ignore
    }
  };

  const getAuthorDisplay = (comment) => {
    const name = comment.author_first_name || comment.author_name || `User #${comment.author_id}`;
    const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    return {
      name,
      isVerified: comment.author_verified,
      initials,
      photo: comment.author_photo,
    };
  };

  const handleUpvote = async (commentId) => {
    if (!isLoggedIn) {
      if (onLoginRequired) onLoginRequired();
      return;
    }

    try {
      const response = await api.post(`/comments/${commentId}/upvote`);
      setComments(comments.map(c => {
        if (c.id !== commentId) return c;
        return {
          ...c,
          upvote_count: response.data.upvote_count,
          user_upvoted: response.data.upvoted,
        };
      }));
    } catch (error) {
      console.error('Error toggling upvote:', error);
    }
  };

  const pinnedInsights = comments.filter(c => (c.upvote_count || 0) >= 5);

  // Thread author display
  const threadAuthor = thread.author_first_name || thread.author_name || `User #${thread.author_id}`;
  const threadInitials = threadAuthor.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#FF8A3D',
            cursor: 'pointer',
            fontSize: '1.25rem',
            padding: '0.5rem'
          }}
        >
          {'\u2190'}
        </button>
        <div>
          <p style={{
            color: '#9ca3af',
            fontSize: '0.85rem',
            margin: '0 0 0.25rem 0'
          }}>
            {boardName}
          </p>
          <h3 style={{
            color: '#FFFFFF',
            fontSize: '1.25rem',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            {thread.is_pinned && <span style={{ color: '#FF8A3D' }}>{'\u{1F4CC}'}</span>}
            {thread.title}
          </h3>
        </div>
        {isLoggedIn && (
          <button
            onClick={toggleFollow}
            aria-label={isFollowing ? 'Unfollow this thread' : 'Follow this thread for updates'}
            aria-pressed={isFollowing}
            style={{
              marginLeft: 'auto',
              padding: '0.35rem 0.85rem',
              background: isFollowing ? 'rgba(255, 138, 61, 0.15)' : 'transparent',
              border: `1px solid ${isFollowing ? '#FF8A3D' : 'rgba(255, 138, 61, 0.3)'}`,
              color: '#FF8A3D',
              borderRadius: '16px',
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {isFollowing ? '\uD83D\uDD14 Following' : '\uD83D\uDD15 Follow'}
          </button>
        )}
      </div>

      {/* Original Post */}
      <div style={{
        background: 'rgba(255, 138, 61, 0.1)',
        border: '1px solid rgba(255, 138, 61, 0.3)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '1rem'
        }}>
          {thread.author_photo ? (
            <img src={thread.author_photo} alt={`${threadAuthor} avatar`} style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              objectFit: 'cover',
            }} />
          ) : (
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: '#FF8A3D',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#0B0B10'
            }}>
              {threadInitials}
            </div>
          )}
          <div>
            <span
              onClick={(e) => {
                e.stopPropagation();
                if (onViewProfile && thread.author_id) onViewProfile(thread.author_id);
              }}
              style={{ color: '#FFFFFF', fontWeight: 600, cursor: onViewProfile ? 'pointer' : 'default' }}
            >
              {threadAuthor}
            </span>
            {thread.author_verified && (
              <span style={{ marginLeft: '0.5rem', color: '#FC4C02', fontSize: '0.75rem', fontWeight: 600 }}>
                Strava {'\u2713'}
              </span>
            )}
            <span style={{ color: '#9ca3af', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
              {'\u2022'} {formatTimeAgo(thread.created_at)}
            </span>
          </div>
        </div>

        {thread.content ? (
          <p style={{
            color: '#FFFFFF',
            fontSize: '1rem',
            lineHeight: 1.6,
            margin: 0,
            whiteSpace: 'pre-wrap'
          }}>
            {thread.content}
          </p>
        ) : (
          <p style={{
            color: '#9ca3af',
            fontStyle: 'italic',
            margin: 0
          }}>
            No additional details provided.
          </p>
        )}
      </div>

      {/* Pinned Insights */}
      {pinnedInsights.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 138, 61, 0.15) 0%, rgba(255, 138, 61, 0.05) 100%)',
          borderLeft: '4px solid #FF8A3D',
          borderRadius: '12px',
          padding: '1.25rem 1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h4 style={{
            color: '#FF8A3D',
            fontSize: '1rem',
            margin: '0 0 1rem 0',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            {'\u{1F4A1}'} Pinned Insights
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {pinnedInsights.map(insight => {
              const author = getAuthorDisplay(insight);
              return (
                <div key={insight.id} style={{
                  background: 'rgba(11, 11, 16, 0.4)',
                  borderRadius: '8px',
                  padding: '1rem'
                }}>
                  <p style={{
                    color: '#FFFFFF',
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                    margin: '0 0 0.5rem 0'
                  }}>
                    {insight.content}
                  </p>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '0.8rem',
                    color: '#8b9cb3'
                  }}>
                    <span>{author.name}</span>
                    <span style={{ color: '#FF8A3D' }}>{'\u25B2'} {insight.upvote_count || 0}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Comments Section */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h4 style={{
          color: '#FFFFFF',
          fontSize: '1rem',
          marginBottom: '1rem'
        }}>
          {comments.length} {comments.length === 1 ? 'Reply' : 'Replies'}
        </h4>

        {loading ? (
          <div role="status" aria-live="polite" style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem' }}>
            Loading replies...
          </div>
        ) : comments.length === 0 ? (
          <div style={{
            color: '#9ca3af',
            textAlign: 'center',
            padding: '2rem',
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '12px'
          }}>
            No replies yet. Be the first to respond!
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {comments.map((comment) => {
              const author = getAuthorDisplay(comment);
              return (
                <div
                  key={comment.id}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 138, 61, 0.15)',
                    borderRadius: '12px',
                    padding: '1.25rem'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    {author.photo ? (
                      <img src={author.photo} alt={`${author.name} avatar`} style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                      }} />
                    ) : (
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: 'rgba(255, 138, 61, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#FF8A3D'
                      }}>
                        {author.initials}
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onViewProfile && comment.author_id) onViewProfile(comment.author_id);
                        }}
                        style={{ color: '#FFFFFF', fontWeight: 500, cursor: onViewProfile ? 'pointer' : 'default' }}
                      >
                        {author.name}
                      </span>
                      {author.isVerified && (
                        <span style={{
                          marginLeft: '0.5rem',
                          color: '#FC4C02',
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}>
                          Strava {'\u2713'}
                        </span>
                      )}
                    </div>
                    <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                      {formatTimeAgo(comment.created_at)}
                    </span>
                  </div>

                  <p style={{
                    color: '#FFFFFF',
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                    margin: '0 0 0.75rem 0',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {comment.content}
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUpvote(comment.id);
                    }}
                    aria-label={`Upvote (${comment.upvote_count || 0})`}
                    aria-pressed={!!comment.user_upvoted}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${comment.user_upvoted ? '#FF8A3D' : 'rgba(139, 156, 179, 0.3)'}`,
                      borderRadius: '16px',
                      padding: '0.3rem 0.75rem',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      color: comment.user_upvoted ? '#FF8A3D' : '#6b7280',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {'\u25B2'} {comment.upvote_count || 0}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reply Input */}
      {isLoggedIn ? (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 138, 61, 0.2)',
          borderRadius: '12px',
          padding: '1rem'
        }}>
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Write a reply..."
            rows={3}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: 'rgba(11, 11, 16, 0.5)',
              border: '1px solid rgba(255, 138, 61, 0.2)',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '1rem',
              outline: 'none',
              resize: 'vertical',
              fontFamily: 'inherit',
              marginBottom: '0.75rem'
            }}
          />
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center'
          }}>
            <button
              onClick={handleSubmitComment}
              disabled={!newComment.trim() || submitting}
              style={{
                padding: '0.5rem 1.25rem',
                background: newComment.trim() && !submitting ? '#FF8A3D' : 'rgba(255, 138, 61, 0.3)',
                border: 'none',
                color: '#0B0B10',
                borderRadius: '8px',
                cursor: newComment.trim() && !submitting ? 'pointer' : 'not-allowed',
                fontSize: '0.9rem',
                fontWeight: 600
              }}
            >
              {submitting ? 'Posting...' : 'Reply'}
            </button>
          </div>
        </div>
      ) : (
        <div style={{
          background: 'rgba(255, 138, 61, 0.1)',
          border: '1px solid rgba(255, 138, 61, 0.3)',
          borderRadius: '12px',
          padding: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <p style={{ color: '#FF8A3D', margin: '0 0 0.25rem 0', fontWeight: 500 }}>
              Sign in to reply
            </p>
            <p style={{ color: '#8b9cb3', fontSize: '0.85rem', margin: 0 }}>
              Join the conversation with other runners
            </p>
          </div>
          <button
            onClick={() => onLoginRequired && onLoginRequired()}
            style={{
              padding: '0.5rem 1.25rem',
              background: '#FF8A3D',
              border: 'none',
              color: '#0B0B10',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 600
            }}
          >
            Sign In
          </button>
        </div>
      )}
    </div>
  );
};

export default Thread;
